import React, { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { BookOpen, Textbox, Hash, CheckCircle, WarningCircle, ArrowsClockwise, Sparkle, ListChecks, UploadSimple } from "@phosphor-icons/react";
import { Progress } from "@/components/ui/progress";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { projectApi } from "@/api";
import { useProject } from "@/pages/ProjectShell";

function Stat({ icon: Icon, label, value, accent }) {
  return (
    <div className="border border-border bg-surface rounded-lg p-5">
      <div className="flex items-center gap-2 text-muted-foreground mb-2">
        <Icon size={16} weight="duotone" className={accent ? "text-primary" : ""} />
        <span className="overline">{label}</span>
      </div>
      <p className="font-serif text-3xl">{value}</p>
    </div>
  );
}

export default function Dashboard() {
  const { pid } = useParams();
  const navigate = useNavigate();
  const { status, startAnalysis, restartAnalysis, loadProject } = useProject();
  const [data, setData] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const load = () => projectApi.dashboard(pid).then(setData).catch(() => {});
  useEffect(() => { load(); }, [pid, status.status, status.done]);

  const doImport = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const res = await projectApi.importFile(pid, fd);
      toast.success(`${res.chapter_count} chapitre(s) importé(s)`);
      await load();
      loadProject?.();
      navigate(`/project/${pid}/structure`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Échec de l'import du fichier");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!data) return <div className="p-8 text-muted-foreground">Chargement…</div>;
  const { manuscript, analysis, progress } = data;
  const empty = manuscript.chapters === 0;

  return (
    <div className="p-8 max-w-5xl fade-up">
      <input
        ref={fileRef}
        type="file"
        accept=".docx,.doc,.txt,.rtf"
        className="hidden"
        onChange={(e) => doImport(e.target.files[0])}
        data-testid="dashboard-import-input"
      />
      <div className="flex items-end justify-between mb-8 flex-wrap gap-3">
        <div>
          <p className="overline text-primary mb-1">Tableau de bord</p>
          <h1 className="font-serif text-4xl font-light tracking-tight">{manuscript.name}</h1>
          <p className="text-sm text-muted-foreground mt-2">
            Dernière analyse : {data.last_analysis_at ? new Date(data.last_analysis_at).toLocaleString("fr") : "jamais"} · Statut : {data.status}
          </p>
        </div>
        <Button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="rounded-sm gap-2"
          data-testid="dashboard-import-cta"
        >
          <UploadSimple size={18} /> {uploading ? "Import en cours…" : (empty ? "Importer un manuscrit" : "Réimporter un fichier")}
        </Button>
      </div>

      {empty ? (
        <div className="border border-dashed border-border rounded-lg py-20 text-center">
          <BookOpen size={44} weight="duotone" className="mx-auto text-muted-foreground mb-3" />
          <p className="font-serif text-2xl mb-2">Aucun manuscrit importé</p>
          <p className="text-muted-foreground mb-6">Importez un fichier DOCX, DOC, TXT ou RTF pour commencer.</p>
          <Button onClick={() => fileRef.current?.click()} disabled={uploading} className="rounded-sm gap-2" data-testid="dashboard-empty-import">
            <UploadSimple size={18} /> {uploading ? "Import en cours…" : "Choisir un fichier"}
          </Button>
        </div>
      ) : (
        <>
          {/* Progress */}
          <div className="border border-border bg-surface rounded-lg p-6 mb-6">
            <div className="flex items-center justify-between mb-3">
              <h2 className="font-serif text-xl">Progression de l'analyse</h2>
              <span className="font-mono text-sm text-muted-foreground">{progress.done} / {progress.total} chapitres analysés</span>
            </div>
            <Progress value={progress.percent} className="h-2.5" />
            <div className="flex items-center justify-between mt-3">
              <span className="text-sm text-muted-foreground">{progress.percent}% terminé</span>
              <div className="flex gap-2">
                {progress.done < progress.total && status.status !== "running" && (
                  <Button size="sm" variant="outline" onClick={startAnalysis} className="rounded-sm gap-2" data-testid="dashboard-analyze">
                    <Sparkle size={16} weight="duotone" /> {progress.done > 0 ? "Reprendre" : "Lancer l'analyse"}
                  </Button>
                )}
                {progress.done > 0 && status.status !== "running" && (
                  <Button size="sm" variant="ghost" onClick={restartAnalysis} className="rounded-sm gap-2" data-testid="dashboard-restart">
                    <ArrowsClockwise size={16} /> Recommencer depuis le début
                  </Button>
                )}
              </div>
            </div>
          </div>

          {/* Manuscript stats */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            <Stat icon={ListChecks} label="Chapitres" value={manuscript.chapters} accent />
            <Stat icon={Textbox} label="Mots" value={(manuscript.words || 0).toLocaleString("fr")} />
            <Stat icon={Hash} label="Caractères" value={(manuscript.characters || 0).toLocaleString("fr")} />
            <Stat icon={CheckCircle} label="Analysés" value={`${analysis.analyzed}/${manuscript.chapters}`} />
          </div>

          {/* Analysis findings */}
          <h2 className="font-serif text-xl mb-3">Résultats de l'analyse</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            <motion.div whileHover={{ y: -2 }} className="border-t-2 rounded-lg border border-border bg-surface p-5" style={{ borderTopColor: "#DC2626" }}>
              <p className="overline text-muted-foreground mb-1">Corrections</p>
              <p className="font-serif text-3xl" style={{ color: "#DC2626" }}>{analysis.corrections}</p>
            </motion.div>
            <motion.div whileHover={{ y: -2 }} className="border-t-2 rounded-lg border border-border bg-surface p-5" style={{ borderTopColor: "#F97316" }}>
              <p className="overline text-muted-foreground mb-1">Suggestions</p>
              <p className="font-serif text-3xl" style={{ color: "#F97316" }}>{analysis.suggestions}</p>
            </motion.div>
            <motion.div whileHover={{ y: -2 }} className="border-t-2 rounded-lg border border-border bg-surface p-5" style={{ borderTopColor: "#EAB308" }}>
              <p className="overline text-muted-foreground mb-1">Répétitions</p>
              <p className="font-serif text-3xl" style={{ color: "#EAB308" }}>{analysis.repetitions}</p>
            </motion.div>
            <motion.div whileHover={{ y: -2 }} onClick={() => navigate(`/project/${pid}/consistency`)} className="cursor-pointer border-t-2 rounded-lg border border-border bg-surface p-5" style={{ borderTopColor: "#4F46E5" }}>
              <p className="overline text-muted-foreground mb-1">Incohérences</p>
              <p className="font-serif text-3xl" style={{ color: "#4F46E5" }}>{analysis.inconsistencies}</p>
            </motion.div>
          </div>

          <div className="flex gap-3 mt-8">
            <Button variant="outline" onClick={() => navigate(`/project/${pid}/editor`)} className="rounded-sm gap-2"><ArrowsClockwise size={16} /> Ouvrir l'éditeur</Button>
            <Button variant="outline" onClick={() => navigate(`/project/${pid}/memory`)} className="rounded-sm">Mémoire du roman</Button>
            <Button variant="outline" onClick={() => navigate(`/project/${pid}/report`)} className="rounded-sm">Rapport éditorial</Button>
          </div>
        </>
      )}
    </div>
  );
}
